import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import { ReturnViewMode } from './return-view.types';
import { ReturnViewService } from './return-view.service';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-common-return-view',
    templateUrl: './common-return-view.component.html',
    styleUrls: ['./common-return-view.component.css'],
    standalone: false
})
export class CommonReturnViewComponent implements OnInit, OnDestroy {

  @Input() mode: ReturnViewMode = 'post-sub';
  @ViewChild('returnSubmissionPopup') returnSubmissionPopup!: TemplateRef<any>;
  @ViewChild('print') print!: ElementRef<HTMLButtonElement>;

  currentLang = 'en';
  apiData: any;
  isShow = false;
  printActive = false;
  isUserTRP = false;
  isReturnSubmissionSuccessful = false;

  taxPayerName = '';
  userTin = '';

  modalRef?: BsModalRef;
  modalRefTrp?: BsModalRef;
  otpForm!: UntypedFormGroup;
  showDetailsReturn = false;
  returnDetailsData: any;
  detailsViewData: any;
  isLoadingDetailsReturn = false;
  isViewTransitioning = false;
  pendingDetailsView: boolean | null = null;
  isProduction:boolean = environment.production;

  readonly modalConfig = {
    backdrop: true,
    ignoreBackdropClick: true,
    class: 'modal-dialog-centered'
  };

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private returnViewService: ReturnViewService,
    private jwtHelper: JwtHelperService,
    private fb: FormBuilder,
    private loader: NgxUiLoaderService
  ) { }

  ngOnInit(): void {
    this.mode = this.route.snapshot.data['mode'] || this.mode;
    this.taxPayerName = localStorage.getItem('name') || '';
    this.userTin = localStorage.getItem('tin') || '';
  // this.isUserTRP = this.isTaxpayerTypeTRP();
    this.createOtpForm();
    this.initializeView();
  }

  hideReturnViewLoader(): void {
    try {
      this.loader.stopAll();
    } catch (e) {
    }
    setTimeout(() => {
      try {
        this.loader.stopAll();
      } catch (e) {
      }
      const overlays = document.querySelectorAll('.ngx-overlay, .ngx-foreground-closing, .ngx-ui-loader');
      for (let i = 0; i < overlays.length; i++) {
        const el = overlays[i] as HTMLElement;
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.hideReturnViewLoader();
  }

  get isPostSubMode(): boolean {
    return this.mode === 'post-sub';
  }

  get isFinalMode(): boolean {
    return this.mode === 'final';
  }

  setLanguage(lang: string): void {
    this.currentLang = lang;
  }

  goBackToTaxAndPayment(): void {
    this.router.navigate(['/user-panel/tax-and-payment']);
  }

  triggerPrint(): void {
    this.printActive = true;

    setTimeout(() => {
      this.print.nativeElement.click();
      this.printActive = false;
    }, 3000);
  }

  proceedToSubmission(): void {
    const payable = this.apiData?.taxPaymentData?.tc_S_Due_Payable;

    if (payable > 0) {
      this.toastr.error(
        `Payment incomplete. Clear your payable amount ${payable} BDT first.`,
        '',
        { timeOut: 2500 }
      );
      return;
    }

    if (this.isReturnSubmissionSuccessful) {
      this.toastr.error('You have already submitted your return online.', '', { timeOut: 2500 });
      return;
    }

    this.openSubmissionConfirmModal();
  }

  openSubmissionConfirmModal(): void {
    this.modalRef = this.modalService.show(this.returnSubmissionPopup, this.modalConfig);
  }

  closeSubmissionModal(): void {
    this.modalRef?.hide();
  }

  confirmSubmission(): void {
    this.closeSubmissionModal();
    this.submitReturnOnline();
  }

  startTrpSubmission(template: TemplateRef<any>): void {
    this.closeSubmissionModal();

    this.returnViewService.sendTrpOtp().subscribe({
      next: (result) => {
        this.toastr.success(result.message, '', { timeOut: 3000 });
      },
      error: (error) => {
      }
    });

    this.modalRefTrp = this.modalService.show(template, this.modalConfig);
  }

  cancelTrpSubmission(): void {
    this.modalRefTrp?.hide();
    this.otpForm.reset();
  }

  submitReturnWithOtp(): void {
    if (this.isUserTRP) {
      this.verifyTrpOtpAndSubmit();
      return;
    }

    this.modalRefTrp?.hide();
    this.submitReturnOnline();
  }

  get otpControls() {
    return this.otpForm.controls;
  }

  private initializeView(): void {
    if (this.isPostSubMode) {
      this.initializePostSubView();
      return;
    }

    this.initializeFinalView();
  }

  private initializePostSubView(): void {
    this.returnViewService.checkReturnView().subscribe({
      next: (result) => {
        if (result) {
          this.loadPostSubSubmissionStatus();
          return;
        }
        this.hideReturnViewLoader();
      },
      error: (error) => {
        this.hideReturnViewLoader();
        if (this.shouldNavigateBackOnCheckError()) {
          this.location.back();
        }

      }
    });
  }

  private initializeFinalView(): void {
    this.returnViewService.getSubmissionStatus().subscribe({
      next: (result) => this.handleFinalSubmissionStatus(result?.replyMessage),
      error: (error) => {
        this.isShow = false;
        this.hideReturnViewLoader();
      }
    });
  }

  private loadPostSubSubmissionStatus(): void {
    this.returnViewService.getSubmissionStatus().subscribe({
      next: (result) => {
        const submissionStatus = result?.replyMessage;

        if (submissionStatus === 'ONLINE') {
          this.isReturnSubmissionSuccessful = true;
          this.isShow = false;
          this.hideReturnViewLoader();
          this.toastr.error('You have already submitted your return online.', '', { timeOut: 2500 });
          return;
        }

        this.loadReturnData();
        this.isReturnSubmissionSuccessful = false;
      },
      error: (error) => {
        this.isShow = false;
        this.hideReturnViewLoader();
      }
    });
  }

  private handleFinalSubmissionStatus(submissionStatus: string): void {
    if (submissionStatus === 'ONLINE') {
      this.loadReturnData(true);
      return;
    }

    this.isShow = false;
    this.hideReturnViewLoader();

    if (submissionStatus === 'ONLINE_PENDING') {
      this.toastr.error('Your return is in draft mode. Submit your return first.');
      return;
    }

    this.toastr.error('Submit your return in online first.', '', { timeOut: 3000 });
  }

  private loadReturnData(redirectSinglePageWhenNeeded = false): void {
    this.returnViewService.getReturnViewData().subscribe({
      next: (response) => {
        this.apiData = response.replyMessage;

        if (
          redirectSinglePageWhenNeeded &&
          this.apiData?.headSummery == null &&
          this.apiData?.singlePageTax
        ) {
          this.isShow = false;
          this.hideReturnViewLoader();
          this.router.navigate(['/user-panel/single-page-return-view']);
          return;
        }

        this.isShow = true;
        this.hideReturnViewLoader();
      },
      error: (error) => {
        this.isShow = false;
        this.hideReturnViewLoader();
      }
    });
  }

  private verifyTrpOtpAndSubmit(): void {
    this.returnViewService.verifyTrpOtp(this.otpForm.value.otpValue).subscribe({
      next: (result) => {
        this.toastr.success(result.message, '', { timeOut: 3000 });
        this.modalRefTrp?.hide();
        this.submitReturnOnline();
      },
      error: (error) => {
        this.otpForm.reset();
      }
    });
  }

  private submitReturnOnline(): void {
    const assessmentYear = this.returnViewService.getCurrentAssessmentYear();

    this.returnViewService.submitReturn(this.userTin, assessmentYear).subscribe({
      next: (result) => {
        if (result.success) {
          this.toastr.success('Return Submitted Successfully', '', { timeOut: 1000 });
          this.router.navigate(['/user-panel/thanks-giving']);
          return;
        }

        this.toastr.error('Return Submission Failed!', '', { timeOut: 2000 });
      },
      error: (error) => {
      }
    });
  }

  private createOtpForm(): void {
    this.otpForm = this.fb.group({
      otpValue: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]]
    });
  }

  private shouldNavigateBackOnCheckError(): boolean {
    return this.mode === 'post-sub';
  }

  private isTaxpayerTypeTRP(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    const decodedToken = this.jwtHelper.decodeToken(token);
    return decodedToken?.claims?.userType === 'TRP_TAXPAYER';
  }

  get isViewSwitching(): boolean {
    return this.isViewTransitioning || this.isLoadingDetailsReturn;
  }

  closeDetailsReturn(): void {
    this.switchToView(false);
  }

  getReturnDetailsData(): void {
    if (this.isViewSwitching || this.showDetailsReturn) {
      return;
    }

    if (this.detailsViewData) {
      this.switchToView(true);
      return;
    }

    this.isLoadingDetailsReturn = true;
    this.returnViewService.getReturnDetailsData().subscribe({
      next: (result) => {
        this.returnDetailsData = result?.replyMessage ?? result;
        this.detailsViewData = {
          ...this.apiData,
          ...this.returnDetailsData
        };
        this.isLoadingDetailsReturn = false;
        this.hideReturnViewLoader();
        this.switchToView(true);
      },
      error: (error) => {
        this.isLoadingDetailsReturn = false;
        this.hideReturnViewLoader();
      }
    });
  }

  private switchToView(showDetails: boolean): void {
    if (this.showDetailsReturn === showDetails || this.isViewTransitioning) {
      return;
    }

    this.pendingDetailsView = showDetails;
    this.isViewTransitioning = true;

    // Short delay so the spinner is perceivable while the heavy view re-renders.
    setTimeout(() => {
      this.showDetailsReturn = showDetails;
      this.isViewTransitioning = false;
      this.pendingDetailsView = null;
    }, 350);
  }
}
